import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import {
  FlatList,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  getChildren,
  getParents,
  Option,
  SearchFilterOption,
} from '@/utils/search-filters';
import { MultiStateCheckbox } from '@/components/multi-state-checkbox';
import { SCREEN_PADDING, SCREEN_PADDING_SMALL } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props<K> {
  buttonRef?: React.RefObject<View | null>;
  filter: SearchFilterOption<K> | null;
  isVisible: boolean;
  onClose: () => void;
  onSelect: (key: K, selectedValues: Set<string>) => void;
  trackingPrefix?: string;
}

export const SearchFiltersActionSheet = <K,>({
  buttonRef,
  onClose,
  isVisible,
  filter,
  onSelect,
  trackingPrefix = 'catalog_search',
}: Props<K>) => {
  const { height } = useWindowDimensions();
  const { isSmallLayout } = useLayoutContext();

  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());
  const [parentValues, setParentValues] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (filter) {
      setSelectedValues(filter.selected);
    }
  }, [filter]);

  const updateParentValues = useCallback((selectedValues: Set<string>) => {
    const parentValues = new Set<string>();

    selectedValues.forEach((value) => {
      getParents(value).forEach((parentValue) => {
        if (!selectedValues.has(parentValue)) {
          parentValues.add(parentValue);
        }
      });
    });

    setParentValues(parentValues);
  }, []);

  useEffect(() => {
    // Set parent values of selected items
    updateParentValues(selectedValues);
  }, [selectedValues, updateParentValues]);

  const toggleNestedValues = (val: string, action: (v: string) => void) => {
    filter?.options.forEach((option: Option) => {
      if (option.value.startsWith(val) && option.value !== val) {
        action(option.value);
      }
    });
  };

  const onItemSelect = (value: string) => {
    setSelectedValues((prev) => {
      const next = new Set(prev);

      const deselectValue = (val: string) => next.delete(val);
      const selectValue = (val: string) => next.add(val);

      if (next.has(value)) {
        next.delete(value);

        // Deselect parents
        getParents(value).forEach((parentValue) => {
          const hasSelectedChildren = getChildren(
            parentValue,
            filter?.options ?? [],
          )?.some((child) => next.has(child.value));

          if (!hasSelectedChildren) {
            next.delete(parentValue);
          }
        });

        // Deselect all nested values
        toggleNestedValues(value, deselectValue);
      } else {
        if (parentValues.has(value)) {
          // Deselect all nested values
          toggleNestedValues(value, deselectValue);
        } else {
          next.add(value);

          // Select all nested values
          toggleNestedValues(value, selectValue);
        }
      }

      return next;
    });
  };

  const renderAsPopover = Boolean(!isSmallLayout && buttonRef?.current);

  if (!filter) {
    return null;
  }

  const renderItem = ({ item }: { item: Option }) => {
    const renderAsParent = parentValues.has(item.value);

    return (
      <View key={item.value}>
        <TouchableOpacity
          activeOpacity={1}
          onPress={() => onItemSelect(item.value)}
          style={styles.listItem}
        >
          <View
            style={[
              styles.item,
              item.depth
                ? { paddingLeft: (item.depth * SCREEN_PADDING) / 2 }
                : null,
            ]}
          >
            <Typography numberOfLines={1} variant="paragraph-base-600">
              {item.title}
            </Typography>
            {item.subtitle ? (
              <Typography variant="paragraph-md">{item.subtitle}</Typography>
            ) : null}
          </View>
          <MultiStateCheckbox
            isPartiallySelected={renderAsParent}
            isSelected={renderAsParent || selectedValues.has(item.value)}
            onSelect={() => onItemSelect(item.value)}
            trackingData={{
              name: `${trackingPrefix}_filters.checkbox`,
              properties: {
                title: item.title,
              },
            }}
          />
        </TouchableOpacity>
      </View>
    );
  };

  const buttonComponents = (
    <View style={styles.buttonWrapper}>
      <View style={styles.fullHeight}>
        <Button
          onPress={() => {
            setSelectedValues(new Set());
            onSelect(filter.key, new Set());
          }}
          size={isSmallLayout ? 'medium' : 'small'}
          trackingData={{
            name: `${trackingPrefix}_filters.clear_filters_button`,
            properties: {
              filterTitle: filter.title,
            },
          }}
          type="tertiary"
        >
          {i18n.t('clear_filters')}
        </Button>
      </View>
      <View style={styles.fullHeight}>
        <Button
          onPress={() => {
            onSelect(filter.key, selectedValues);
          }}
          size={isSmallLayout ? 'medium' : 'small'}
          trackingData={{
            name: `${trackingPrefix}_filters.apply_filters_button`,
            properties: {
              filterTitle: filter.title,
            },
          }}
        >
          {i18n.t('apply')}
        </Button>
      </View>
    </View>
  );

  return (
    <BaseActionSheet
      contentStyle={[
        styles.wrapper,
        { maxHeight: height * 0.7 },
        renderAsPopover ? styles.popoverWrapper : null,
      ]}
      isVisible={isVisible}
      onClose={onClose}
      popoverTarget={buttonRef?.current}
      testID={`search-filters-${filter.key}`}
      title={filter.title}
    >
      {isSmallLayout ? (
        <View style={styles.filterTitle}>
          <Typography variant="heading-lg">{filter.title}</Typography>
        </View>
      ) : null}
      <FlatList
        bounces={false}
        contentContainerStyle={renderAsPopover ? styles.listContent : null}
        data={filter.options}
        keyExtractor={(item) => item.value as string}
        renderItem={renderItem}
        style={renderAsPopover ? styles.list : null}
      />
      {buttonComponents}
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  buttonWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: SCREEN_PADDING,
    paddingTop: SCREEN_PADDING / 2,
  },
  filterTitle: {
    marginBottom: SCREEN_PADDING / 2,
    marginLeft: SCREEN_PADDING,
  },
  fullHeight: {
    flex: 1,
  },
  item: {
    flex: 1,
  },
  list: {
    maxHeight: 420,
  },
  listContent: {
    paddingTop: 12,
  },
  listItem: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    paddingHorizontal: SCREEN_PADDING,
    paddingVertical: 12,
  },
  popoverWrapper: {
    width: 320,
  },
  wrapper: {
    paddingHorizontal: 0,
    paddingVertical: SCREEN_PADDING_SMALL,
  },
});
